import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../lib/auth';

/**
 * 用户菜单：用户名 / 角色标识 / 退出登录。
 *
 * 角色徽标刻意可见：手动同步（会大量请求上游）仅 admin 可用，
 * 让用户一眼知道自己有没有这个权限，避免看到按钮却点不动。
 */
export function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;
  const isAdmin = user.role === 'admin';

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`账户菜单，当前 ${user.username}`}
        className="flex h-7 items-center gap-1.5 rounded px-2 text-[12px] transition-colors hover:bg-[var(--bg-hover)]"
        style={{ color: 'var(--text-2)' }}
      >
        <span
          className="grid h-4 w-4 place-items-center rounded-full text-[9px] font-bold"
          style={{ background: 'var(--accent)', color: '#fff' }}
          aria-hidden
        >
          {user.username.slice(0, 1).toUpperCase()}
        </span>
        <span className="hidden sm:inline">{user.username}</span>
        {isAdmin && (
          <span
            className="rounded px-1 text-[9px]"
            style={{ background: 'var(--bg-elevated)', color: 'var(--star)' }}
            title="管理员：可触发手动同步"
          >
            管理员
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-8 z-50 w-44 rounded-[8px] border py-1 shadow-lg"
          style={{ background: 'var(--bg-surface)', boxShadow: 'var(--shadow-card)' }}
        >
          <div className="border-b px-3 py-2">
            <div className="text-[12px] font-medium" style={{ color: 'var(--text-1)' }}>
              {user.displayName || user.username}
            </div>
            <div className="num text-[11px]" style={{ color: 'var(--text-3)' }}>
              @{user.username} · {isAdmin ? '管理员' : '普通用户'}
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); void logout(); }}
            className="block w-full px-3 py-2 text-left text-[12px] transition-colors hover:bg-[var(--bg-hover)]"
            style={{ color: 'var(--text-2)' }}
          >
            退出登录
          </button>
        </div>
      )}
    </div>
  );
}
